import React, { useEffect, useRef, useState } from 'react';
import { Button, Col, Form, Modal, Row } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { SingleUploadArea } from '../../../components/SingleUploadArea';
import { AlertError } from '../../../components/bootstrap/AlertError';
import Loading from '../../../components/general/Loading';
import { CandidatureState } from '../../../models/CandidatureState';
import { DocumentType } from '../../../models/DocumentType';
import {
	deleteCandidatureChangeRequestDocument,
	listCandidatureChangeRequestDocumentsByType,
	submitCandidatureChangeRequestContestation,
	submitInitialDecision,
	uploadCandidatureChangeRequestDocument,
} from '../../../rest/candidatureChangeRequest';
import { handleError, isNotBusinessError } from '../../../utils/handleError';
import { Formik } from 'formik';
import * as yup from 'yup';
import { MultipleUploadArea } from '../../../components/MultipleUploadArea';
import { TextAreaInputField } from '../../../components/bootstrap/TextAreaInputField';

const validationSchema = yup.object().shape({
	contestationMotive: yup
		.string()
		.required(<FormattedMessage id='errors.fieldRequiredText' />),
});

export function CandidatureChangeRequestContestation({
	candidatureChange,
	setShowModal,
}) {
	const {
		candidatureExternalId: candidatureExternalId,
		externalId: externalId,
	} = useParams();

	//errors
	const [error, setError] = useState(null);

	//formik
	const [formikInitialValues, setFormikInitialValues] = useState(null);
	const formikPropsRef = useRef(null);

	//form control
	const [loading, setLoading] = useState(true);
	const [submited, setSubmited] = useState(false);

	const [documents, setDocuments] = useState([]);

	const navigate = useNavigate();
	const intl = useIntl();

	const docsUploadCallback = async (formData) => {
		return await uploadCandidatureChangeRequestDocument(
			candidatureExternalId,
			externalId,
			formData
		);
	};

	const docsDeleteCallback = async (documentExternalId) => {
		return await deleteCandidatureChangeRequestDocument(
			candidatureExternalId,
			externalId,
			documentExternalId
		);
	};

	async function fetchData() {
		try {
			let { data: requestDocuments } =
				await listCandidatureChangeRequestDocumentsByType(
					DocumentType.CANDIDATURE_CHANGE_CONTESTATION_ANNEX,
					candidatureExternalId,
					externalId
				);

			let formDocuments = [];

			requestDocuments.forEach((d) => {
				let doc = {
					externalId: d.externalId,
					documentType: d.documentType,
					documentPath: d.documentPath,
					name: d.name,
					content: '',
					submited: true,
					error: '',
					fileDescription: d.fileDescription,
				};

				formDocuments.push(doc);
			});
			setDocuments(formDocuments);

			setFormikInitialValues({ contestationMotive: '' });
		} catch (error) {
			setError(error);
		} finally {
			setLoading(false);
		}
	}

	useEffect(() => {
		fetchData();
	}, []);

	if (submited) {
		sessionStorage.setItem(
				'saveContestation',
				true
			);

		//refresh page
		navigate(0)
	}

	if (error && isNotBusinessError(error)) {
		return handleError(error);
	}

	async function handleSubmit() {
		try {
			await submitCandidatureChangeRequestContestation(
				candidatureExternalId,
				externalId,
				formikPropsRef.current.values
			);
			setSubmited(true);
		} catch (err) {
				setError(err);
		}
	}

	async function validateFormAndSubmit(){
		let errors = await validateForm();
		if (!errors) {
			handleSubmit(true);
		}
	}

	async function validateForm() {
		let errors = false;

		const formikErrors = await formikPropsRef.current.validateForm();

		if(Object.keys(formikErrors).length > 0){
			errors = true;
		}

		return errors;
	}

	function renderForm(formikProps) {
		formikPropsRef.current = formikProps;
		let formikValues = formikProps.values;

		return (
			<>
				<Row>
					<Col>
						<TextAreaInputField
							labelId={'candidatureChangeRequest.contestation.contestationMotive'}
							name={'contestationMotive'}
							handleChange={formikProps.handleChange}
							value={formikValues.contestationMotive}
							isInvalid={formikProps.errors.contestationMotive}
							errorMessage={formikProps.errors.contestationMotive}
							placeholder={intl.formatMessage(
								{
									id: 'annualComplementaryActivitiesPlanForm.characters.placeholder',
								},
								{ nChars: '5000' }
							)}
						/>
					</Col>
				</Row>
				<Row>
					<Col>
						<MultipleUploadArea
							documents={documents}
							setDocuments={setDocuments}
							documentType='CANDIDATURE_CHANGE_CONTESTATION_ANNEX'
							uploadCallback={docsUploadCallback}
							deleteCallback={docsDeleteCallback}
						/>
					</Col>
				</Row>
			</>
		);
	}

	return (
		<Modal
			show={true}
			dialogClassName='modal-65'
			onHide={() => setShowModal(false)}
		>
			<Modal.Header closeButton>
				<FormattedMessage
					id={`candidature.button.contest`}
				/>
			</Modal.Header>
			<Modal.Body>
				{loading ? (
					<span>
						<Loading isDialog/>
					</span>
				) : (
					<Row>
						<Col>
							<Formik
								initialValues={formikInitialValues}
								validateOnBlur={false}
								validateOnChange={false}
								validationSchema={validationSchema}
								onSubmit={handleSubmit}
							>
								{(formikProps) => (
									<Form onSubmit={formikProps.handleSubmit}>
										{renderForm(formikProps)}
									</Form>
								)}
							</Formik>
						</Col>
					</Row>
				)}
			</Modal.Body>
			<Modal.Footer className='d-flex justify-content-between'>
				<Button variant='outline-secondary' onClick={() => setShowModal(false)}>
					<FormattedMessage id='all.cancel' />
				</Button>
				<Button
					variant='primary'
					onClick={validateFormAndSubmit}
				>
					<FormattedMessage id='all.submitButtonText' />
				</Button>
			</Modal.Footer>
		</Modal>
	);
}
